import { Box, Button, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import React from "react";
import { ExerciseAsset } from "../exercise-library/types";
import {
  INVALIDATE_KEYS_ON_EXERCISE_LIST_UPDATE,
  SELECTED_EXERCISE,
} from "../exercise-library/hooks/useArchiveRestoreLibraryExercise";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ExerciseAssetsService from "../../services/ExerciseAssetsService";
import {
  ExerciseAssetStatus,
  LibraryPrivacy,
} from "@growth-machine-llc/stridist-api-client";
import { defaultExerciseWithTitle } from "./utils";
import { WorkoutExercise } from "./types";
import { useToastAlert } from "../app/ToastAlert/ToastAlertProvider";
import { generateId } from "../new-editor/utils/nodeUtil";

const useStyles = makeStyles((theme) => ({
  saveButton: {
    borderWidth: "2px !important",
    borderStyle: "solid",
    padding: theme.spacing(1.25, 0),
    fontWeight: 700,
    fontSize: 14,
    "&[disabled]": {
      backgroundColor: theme.palette.text.secondary,
      color: theme.palette.common.white,
    },
  },
}));

interface SuggestExerciseContainerProps {
  exerciseAsset: ExerciseAsset;
  coachId?: number;
  onComplete?: (exercise: WorkoutExercise) => void;
}

const EXERCISE_SUGGESTED_MESSAGE = "Exercise suggested.";
const EXACT_EXERCISE_NAME = true;

export const SuggestExerciseContainer = (
  props: SuggestExerciseContainerProps,
) => {
  const s = useStyles();
  const { exerciseAsset, coachId, onComplete } = props;

  const queryClient = useQueryClient();
  const { showToastAlert } = useToastAlert();

  const name = exerciseAsset?.exercise?.title;

  const { data: matchedLibraryExercises, isFetching } = useQuery({
    queryKey: [SELECTED_EXERCISE, { name }],
    queryFn: ({ pageParam = 1 }) =>
      // Select active (published or pending) exercise assets private coach's exercise assets with the same name.
      ExerciseAssetsService.getExerciseAssets(
        pageParam as number,
        1,
        name?.trim(),
        // published or pending
        ExerciseAssetStatus.ACTIVE,
        null,
        null,
        null,
        // private coach's exercise assets (has coachId)
        LibraryPrivacy.PRIVATE,
        null,
        EXACT_EXERCISE_NAME,
      ),
    enabled: name?.trim()?.length > 0,
  });

  const activeExerciseAsset = matchedLibraryExercises?.items?.[0];

  const { mutate: suggestExercise, isPending } = useMutation({
    mutationFn: ExerciseAssetsService.suggestExerciseAsset,
    mutationKey: ["suggest-exercise"],
  });

  const handleSuggestExercise = React.useCallback(() => {
    if (activeExerciseAsset) {
      const content = activeExerciseAsset?.content
        ? JSON.parse(activeExerciseAsset.content)
        : defaultExerciseWithTitle(exerciseAsset?.exercise?.title);

      onComplete?.({
        ...content,
        id: generateId(),
        assetId: activeExerciseAsset.id,
      });
    } else {
      suggestExercise(
        {
          name,
        },
        {
          onSuccess: (exerciseAsset) => {
            showToastAlert("success", {
              message: EXERCISE_SUGGESTED_MESSAGE,
            });

            onComplete?.({
              ...JSON.parse(exerciseAsset.content),
              assetId: exerciseAsset.id,
              id: generateId(),
            });
            INVALIDATE_KEYS_ON_EXERCISE_LIST_UPDATE.forEach((queryKey) => {
              queryClient.invalidateQueries({
                queryKey: [queryKey],
                exact: false,
              });
            });
          },
          onError(error) {
            showToastAlert("error", {
              message: error.message,
            });
          },
        },
      );
    }
  }, [isFetching, activeExerciseAsset, queryClient, isPending]);

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}
    >
      <Typography>
        This exercise will be sent to your coach for review.
      </Typography>
      <Button
        variant="contained"
        size="small"
        className={s.saveButton}
        fullWidth
        disabled={isPending || isFetching}
        onClick={handleSuggestExercise}
      >
        Suggest exercise
      </Button>
    </Box>
  );
};
